import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Zap, AlertTriangle, CheckCircle2, Users } from "lucide-react";
import { Card, Button, Skeleton } from "../components/ui";
import developerAPI from "../api/developer-api";
import { useApp } from "../App";
import { DOCS_URL } from "../constants";

function MetricCard({ label, value, icon: Icon, isStatus }) {
  return (
    <Card className="relative overflow-hidden group">
      <div className="flex justify-between items-start mb-4">
        <div className={`p-2 rounded-lg ${isStatus ? "bg-emerald-50 text-emerald-600" : "bg-slate-50 text-slate-600 group-hover:bg-grigora-50 group-hover:text-grigora-600"} transition-colors`}>
          <Icon size={18} />
        </div>
      </div>
      <div>
        <h3 className="text-2xl font-bold text-slate-900 tracking-tight">{value}</h3>
        <p className="text-xs text-slate-500 font-medium mt-1">{label}</p>
      </div>
    </Card>
  );
}

export function Dashboard() {
  const navigate = useNavigate();
  const { apps, loading: appsLoading } = useApp();
  const [statsState, setStatsState] = useState({ loading: true, data: [] });

  useEffect(() => {
    if (apps.length === 0) {
      setStatsState({ loading: false, data: [] });
      return;
    }
    setStatsState((prev) => ({ ...prev, loading: true }));
    const loadStats = async () => {
      try {
        const statsPromises = apps.map((app) => developerAPI.getAppStats(app.id, 7).catch(() => []));
        const results = await Promise.all(statsPromises);
        const flatStats = results.flat();
        setStatsState({ loading: false, data: flatStats });
      } catch (err) {
        console.error("Failed to load stats:", err);
        setStatsState((prev) => ({ ...prev, loading: false }));
      }
    };
    loadStats();
  }, [apps]);

  const allStats = statsState.data;
  const statsLoading = statsState.loading;
  const loading = appsLoading || statsLoading;

  const totalRequests = allStats.reduce((acc, s) => acc + (s.requests_count || 0), 0);
  const totalErrors = allStats.reduce((acc, s) => acc + (s.errors_count || 0), 0);
  const errorRate = totalRequests > 0 ? ((totalErrors / totalRequests) * 100).toFixed(2) : "0.00";

  const chartDataMap = new Map();
  allStats.forEach((s) => {
    const existing = chartDataMap.get(s.date) || { date: s.date, reqs: 0, errors: 0 };
    existing.reqs += s.requests_count || 0;
    existing.errors += s.errors_count || 0;
    chartDataMap.set(s.date, existing);
  });
  const chartData = Array.from(chartDataMap.values()).sort((a, b) => a.date.localeCompare(b.date));

  if (loading) {
    return (
      <div className="p-8 max-w-7xl mx-auto space-y-8">
        <div>
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-72" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <div className="flex justify-between items-start mb-4">
                <Skeleton className="h-9 w-9 rounded-lg" />
              </div>
              <Skeleton className="h-8 w-20 mb-2" />
              <Skeleton className="h-4 w-24" />
            </Card>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <Card className="col-span-2 min-h-[400px]">
            <div className="flex justify-between items-center mb-6">
              <div>
                <Skeleton className="h-4 w-28 mb-2" />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
            <div className="h-[320px] w-full flex items-center justify-center">
              <Skeleton className="h-full w-full rounded-lg" />
            </div>
          </Card>
          <Card className="col-span-1">
            <Skeleton className="h-4 w-28 mb-4" />
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-10 w-full rounded-lg" />
              ))}
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Dashboard</h2>
        <p className="text-slate-500 mt-1">Overview of your app usage and metrics.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <MetricCard label="Total Apps" value={String(apps.length)} icon={Zap} />
        <MetricCard label="Total Requests (7d)" value={totalRequests.toLocaleString()} icon={Users} />
        <MetricCard label="Error Rate" value={`${errorRate}%`} icon={AlertTriangle} />
        <MetricCard label="System Status" value="Healthy" icon={CheckCircle2} isStatus />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <Card className="col-span-2 min-h-[400px]">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-base font-semibold text-slate-900">API Activity</h3>
              <p className="text-sm text-slate-500">Last 7 Days</p>
            </div>
          </div>
          <div className="h-[320px] w-full">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorReqs" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#623ce7" stopOpacity={0.1} />
                      <stop offset="95%" stopColor="#623ce7" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ backgroundColor: "#fff", border: "1px solid #e2e8f0", borderRadius: "8px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)" }} />
                  <Area type="monotone" dataKey="reqs" stroke="#623ce7" strokeWidth={2} fillOpacity={1} fill="url(#colorReqs)" name="Requests" />
                  <Area type="monotone" dataKey="errors" stroke="#ef4444" strokeWidth={1.5} fill="transparent" name="Errors" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-slate-400 text-sm">
                No usage data yet. Create an app to get started.
              </div>
            )}
          </div>
        </Card>
        <Card className="col-span-1">
          <h3 className="text-base font-semibold text-slate-900 mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <Button variant="secondary" className="w-full justify-start" onClick={() => navigate("/apps/new")}>Create New App</Button>
            <Button variant="secondary" className="w-full justify-start" onClick={() => navigate("/apps")}>View My Apps</Button>
            <Button variant="secondary" className="w-full justify-start" onClick={() => window.location.assign(DOCS_URL)}>Read Documentation</Button>
          </div>
          {apps.length === 0 && (
            <div className="mt-6 bg-grigora-50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-grigora-900 mb-1">Get Started</h4>
              <p className="text-xs text-grigora-700 mb-3">Create your first app to start building integrations with Grigora.</p>
              <Button className="w-full text-xs" onClick={() => navigate("/apps/new")}>Create First App</Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
